import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field, BigButton } from "@/components/Field";
import { SelectField, TextArea } from "@/components/Bits";
import { api, errMsg } from "@/lib/api";

export const TIPOS_DOC = { laudo: "Laudo", contrato: "Contrato", manual: "Manual", certificado: "Certificado", nota_fiscal: "Nota fiscal", outro: "Outro" };
const ACCEPT = ".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx";

export const DocumentoUploadDialog = ({ open, onOpenChange, fixed = {}, onSaved }) => {
  const [form, setForm] = useState({});
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [fazendas, setFazendas] = useState([]);
  const [equips, setEquips] = useState([]);
  const ref = useRef();
  const fixedKey = JSON.stringify(fixed);

  useEffect(() => {
    if (!open) return;
    const f = JSON.parse(fixedKey);
    setForm({ tipo: "laudo", titulo: "", descricao: "", cliente_id: f.cliente_id || "", fazenda_id: f.fazenda_id || "", equipamento_id: f.equipamento_id || "" });
    setFile(null);
    api.get("/clientes").then(({ data }) => setClientes(data)).catch(() => {});
  }, [open, fixedKey]);

  useEffect(() => {
    if (!open || !form.cliente_id) { setFazendas([]); return; }
    api.get("/fazendas", { params: { cliente_id: form.cliente_id } }).then(({ data }) => setFazendas(data)).catch(() => {});
  }, [open, form.cliente_id]);

  useEffect(() => {
    if (!open || !form.fazenda_id) { setEquips([]); return; }
    api.get("/equipamentos", { params: { fazenda_id: form.fazenda_id } }).then(({ data }) => setEquips(data)).catch(() => {});
  }, [open, form.fazenda_id]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Selecione o arquivo");
    if (file.size > 20 * 1024 * 1024) return toast.error("Arquivo muito grande (máx. 20MB)");
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    Object.entries(form).forEach(([k, v]) => v && fd.append(k, v));
    try {
      await api.post("/documentos", fd);
      toast.success("Documento enviado");
      onOpenChange(false);
      onSaved();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-3xl max-h-[92vh] overflow-y-auto" data-testid="doc-upload-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Enviar documento</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <SelectField label="Tipo *" id="d-tipo" value={form.tipo || "laudo"} onChange={set("tipo")} options={Object.entries(TIPOS_DOC).map(([value, label]) => ({ value, label }))} data-testid="doc-form-tipo" />
          <Field label="Título *" id="d-tit" required minLength={2} value={form.titulo || ""} onChange={set("titulo")} data-testid="doc-form-titulo" />
          <SelectField label="Cliente *" id="d-cli" required placeholder="Selecione o cliente" disabled={!!fixed.cliente_id} value={form.cliente_id || ""}
            onChange={(e) => setForm({ ...form, cliente_id: e.target.value, fazenda_id: "", equipamento_id: "" })}
            options={clientes.map((c) => ({ value: c.cliente_id, label: c.nome }))} data-testid="doc-form-cliente" />
          <SelectField label="Fazenda (opcional)" id="d-faz" placeholder="Documento geral do cliente" disabled={!!fixed.fazenda_id || !form.cliente_id} value={form.fazenda_id || ""}
            onChange={(e) => setForm({ ...form, fazenda_id: e.target.value, equipamento_id: "" })}
            options={fazendas.map((f) => ({ value: f.fazenda_id, label: f.nome }))} data-testid="doc-form-fazenda" />
          <SelectField label="Equipamento (opcional)" id="d-eq" placeholder="Documento da fazenda" disabled={!!fixed.equipamento_id || !form.fazenda_id} value={form.equipamento_id || ""}
            onChange={set("equipamento_id")} options={equips.map((x) => ({ value: x.equipamento_id, label: `${x.titulo} (${x.codigo})` }))} data-testid="doc-form-equipamento" />
          <TextArea label="Descrição" id="d-desc" rows={2} value={form.descricao || ""} onChange={set("descricao")} data-testid="doc-form-descricao" />
          <input ref={ref} type="file" accept={ACCEPT} className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="doc-form-file" />
          <BigButton type="button" variant="outline" onClick={() => ref.current.click()} data-testid="doc-form-file-button">
            <Upload className="h-5 w-5" /> <span className="truncate">{file ? file.name : "Escolher arquivo (PDF, imagem, Word, Excel)"}</span>
          </BigButton>
          <BigButton type="submit" disabled={busy} data-testid="doc-form-submit">{busy && <Loader2 className="h-5 w-5 animate-spin" />} Enviar</BigButton>
        </form>
      </DialogContent>
    </Dialog>
  );
};
